import { z } from "zod";
import { IsoTimestampSchema, UuidSchema, paginatedResponse } from "./common.js";
import { ContactResponseSchema } from "./crm.js";
export const LeadStatusSchema = z.enum([
    "new",
    "contacted",
    "qualified",
    "booked",
    "closed",
    "lost",
]);
export const LeadMessageResponseSchema = z.object({
    id: UuidSchema,
    lead_id: UuidSchema,
    direction: z.enum(["inbound", "outbound"]),
    channel: z.enum(["sms", "email", "internal_note"]),
    body: z.string(),
    external_id: z.string().nullable(),
    sent_at: IsoTimestampSchema,
    metadata: z.record(z.unknown()),
});
export const LeadResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    contact_id: UuidSchema,
    status: LeadStatusSchema,
    source: z.string().nullable(),
    source_ref: z.string().nullable(),
    assigned_to: UuidSchema.nullable(),
    notes: z.string().nullable(),
    metadata: z.record(z.unknown()),
    first_contacted_at: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const LeadDetailResponseSchema = LeadResponseSchema.extend({
    contact: ContactResponseSchema,
    recent_messages: z.array(LeadMessageResponseSchema),
});
export const CreateLeadRequestSchema = z
    .object({
    phone: z
        .string()
        .regex(/^\+[1-9]\d{1,14}$/, "Phone must be in E.164 format (e.g. +15551234567)")
        .optional(),
    email: z.string().email().optional(),
    first_name: z.string().max(100).optional(),
    last_name: z.string().max(100).optional(),
    company: z.string().max(200).optional(),
    source: z.string().max(50).optional(),
    external_id: z.string().max(200).optional(),
    metadata: z.record(z.unknown()).default({}),
})
    .refine((v) => v.phone !== undefined || v.email !== undefined, {
    message: "At least one of phone or email is required",
});
export const CreateLeadResponseSchema = z.object({
    lead_id: UuidSchema,
    contact_id: UuidSchema,
});
export const UpdateLeadStatusRequestSchema = z.object({
    status: LeadStatusSchema,
});
export const UpdateLeadRequestSchema = z
    .object({
    assigned_to: UuidSchema.nullable().optional(),
    notes: z.string().max(10_000).optional(),
    metadata: z.record(z.unknown()).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
export const CreateLeadMessageRequestSchema = z.object({
    direction: z.enum(["inbound", "outbound"]),
    channel: z.enum(["sms", "email", "internal_note"]),
    body: z.string().min(1).max(10_000),
    metadata: z.record(z.unknown()).default({}),
});
export const LeadAnalyticsResponseSchema = z.object({
    total: z.number().int(),
    status_counts: z.record(z.string(), z.number().int()),
    volume_by_day: z.array(z.object({ date: z.string(), count: z.number().int() })),
});
export const LeadListResponseSchema = paginatedResponse(LeadResponseSchema);
export const LeadMessageListResponseSchema = paginatedResponse(LeadMessageResponseSchema);
// ─── Lead inbox (L3) ─────────────────────────────────────────────────────────
export const InboxLeadResponseSchema = LeadResponseSchema.extend({
    last_message_body: z.string().nullable(),
    last_message_at: z.string().nullable(),
});
export const InboxLeadListResponseSchema = z.object({
    items: z.array(InboxLeadResponseSchema),
    next_cursor: z.string().optional(),
});
// ─── Sequences (L3) ──────────────────────────────────────────────────────────
export const LeadSequenceResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    lead_id: UuidSchema,
    template_name: z.string(),
    status: z.enum(["active", "paused", "completed", "cancelled"]),
    metadata: z.record(z.unknown()),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const LeadSequenceListResponseSchema = z.object({
    items: z.array(LeadSequenceResponseSchema),
});
export const SequenceStepResponseSchema = z.object({
    id: UuidSchema,
    sequence_id: UuidSchema,
    step_number: z.number().int(),
    scheduled_at: IsoTimestampSchema,
    sent_at: IsoTimestampSchema.nullable(),
    status: z.enum(["pending", "sent", "failed", "skipped"]),
    channel: z.string(),
    message_body: z.string(),
    created_at: IsoTimestampSchema,
});
export const SequenceStepListResponseSchema = z.object({
    items: z.array(SequenceStepResponseSchema),
});
export const StartSequenceRequestSchema = z.object({
    template_name: z.enum(["initial_response", "quote_follow_up", "re_engagement"]),
    metadata: z.record(z.unknown()).default({}),
});
export const UpdateSequenceStatusRequestSchema = z.object({
    status: z.enum(["paused", "active", "cancelled"]),
});
//# sourceMappingURL=leads.js.map